<script setup>
// Phase 2.9.8 K*.3 — componente reusavel de Quick Action buttons.
// Fetch das actions configuradas + renderiza grid de botoes + abre modal
// se action tem prompts + dispara POST /execute com o contexto.
//
// Props:
//   contactId (obrigatorio)
//   conversationId (opcional, display_id)
//   kanbanItemId (opcional)
//   context: 'conversation' | 'kanban_detail' | 'kanban_agenda'
//     Filtra server-side quais actions aparecem (via show_in_* flags).
import { ref, computed, onMounted, watch } from 'vue';
import QuickActionsAPI from 'dashboard/api/quickActions';
import { useStore } from 'vuex';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], default: null },
  kanbanItemId: { type: [Number, String], default: null },
  context: { type: String, default: 'conversation' }, // conversation | kanban_detail | kanban_agenda
});

const emit = defineEmits(['executed']);

const store = useStore();
const accountId = computed(
  () => store.state?.auth?.currentUser?.account_id || 1
);

const actions = ref([]);
const loading = ref(true);
const modalAction = ref(null); // action cujo modal esta aberto
const promptValues = ref({});
const executing = ref(false);
const lastResult = ref(null);

// Tailwind color → utility classes
const COLOR_CLASSES = {
  emerald: 'bg-emerald-500 hover:bg-emerald-600 text-white',
  blue: 'bg-blue-500 hover:bg-blue-600 text-white',
  violet: 'bg-violet-500 hover:bg-violet-600 text-white',
  amber: 'bg-amber-500 hover:bg-amber-600 text-white',
  rose: 'bg-rose-500 hover:bg-rose-600 text-white',
  red: 'bg-red-500 hover:bg-red-600 text-white',
  slate: 'bg-n-slate-9 hover:bg-n-solid-3 text-white',
};

// Filtra actions pelo contexto atual
const filtered = computed(() => {
  const prop = `show_in_${props.context}`;
  return actions.value.filter(a => a.enabled && a[prop]);
});

async function load() {
  loading.value = true;
  try {
    const { data } = await QuickActionsAPI.get();
    actions.value = data.quick_actions || [];
  } catch (_e) {
    actions.value = [];
  } finally {
    loading.value = false;
  }
}
onMounted(load);
watch(() => props.contactId, load);

function colorClass(a) {
  return COLOR_CLASSES[a.color] || COLOR_CLASSES.slate;
}

function clickAction(action) {
  if (!action.prompts || action.prompts.length === 0) {
    // Sem prompts: executa direto
    execute(action, {});
    return;
  }
  // Abre modal
  modalAction.value = action;
  promptValues.value = {};
  action.prompts.forEach(p => {
    promptValues.value[p.key] = p.type === 'enum' ? p.options?.[0] || '' : '';
  });
}

function closeModal() {
  modalAction.value = null;
  promptValues.value = {};
}

async function confirmModal() {
  // Valida required
  const missing = modalAction.value.prompts.filter(
    p => p.required && !String(promptValues.value[p.key] || '').trim()
  );
  if (missing.length) {
    alert(`Preencha: ${missing.map(p => p.label).join(', ')}`);
    return;
  }
  await execute(modalAction.value, promptValues.value);
  closeModal();
}

async function execute(action, values) {
  executing.value = true;
  try {
    const { data } = await QuickActionsAPI.execute(action.id, {
      contact_id: props.contactId,
      conversation_id: props.conversationId,
      kanban_item_id: props.kanbanItemId,
      prompt_values: values,
    });
    lastResult.value = {
      action: action.name,
      success: data.success,
      at: Date.now(),
    };
    emit('executed', { action, result: data });
    setTimeout(() => {
      lastResult.value = null;
    }, 3000);
  } catch (e) {
    alert(e?.response?.data?.error || 'Erro ao executar ação');
  } finally {
    executing.value = false;
  }
}
</script>

<template>
  <div v-if="!loading" class="quick-action-buttons space-y-2">
    <!-- Empty state com CTA -->
    <div
      v-if="!filtered.length"
      class="text-xs text-n-slate-11 px-3 py-2 rounded-xl border border-dashed border-n-weak text-center"
    >
      <p class="mb-1">Nenhuma acao rapida configurada</p>
      <a
        :href="`/app/accounts/${accountId}/kanban?view=quick_actions`"
        class="text-woot-500 hover:underline"
        >+ Configurar acoes rapidas</a>
    </div>
    <!-- Grid de botões -->
    <div class="grid grid-cols-2 gap-2">
      <button
        v-for="a in filtered"
        :key="a.id"
        type="button"
        class="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition disabled:opacity-50 min-w-0"
        :class="colorClass(a)"
        :disabled="executing"
        @click="clickAction(a)"
      >
        <span class="text-base shrink-0">{{ a.icon }}</span>
        <span class="truncate">{{ a.name }}</span>
      </button>
    </div>

    <!-- Flash de resultado -->
    <p
      v-if="lastResult"
      class="text-[11px] text-center"
      :class="lastResult.success ? 'text-emerald-600' : 'text-rose-600'"
    >
      {{ lastResult.success ? '✓' : '✗' }} {{ lastResult.action }}
    </p>

    <!-- Modal de prompts -->
    <teleport to="body">
      <div
        v-if="modalAction"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
        @click.self="closeModal"
      >
        <div
          class="bg-n-solid-2 rounded-2xl border border-n-weak p-5 w-full max-w-md space-y-3 shadow-xl"
        >
          <header class="flex items-center gap-2">
            <span class="text-2xl">{{ modalAction.icon }}</span>
            <h3 class="text-lg font-extrabold text-n-slate-12">
              {{ modalAction.name }}
            </h3>
          </header>

          <div v-for="p in modalAction.prompts" :key="p.key">
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              {{ p.label
              }}<span v-if="p.required" class="text-rose-500">*</span>
            </label>
            <select
              v-if="p.type === 'enum'"
              v-model="promptValues[p.key]"
              class="w-full rounded border border-n-weak bg-white bg-n-solid-3 px-3 py-2 text-sm"
            >
              <option v-for="opt in p.options || []" :key="opt" :value="opt">
                {{ opt }}
              </option>
            </select>
            <input
              v-else-if="p.type === 'number'"
              v-model="promptValues[p.key]"
              type="number"
              step="any"
              class="w-full rounded border border-n-weak bg-white bg-n-solid-3 px-3 py-2 text-sm"
            />
            <input
              v-else
              v-model="promptValues[p.key]"
              type="text"
              class="w-full rounded border border-n-weak bg-white bg-n-solid-3 px-3 py-2 text-sm"
            />
          </div>

          <div
            class="flex items-center justify-end gap-2 pt-3 border-t border-n-weak"
          >
            <button
              class="px-4 py-2 rounded border border-n-weak text-sm"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button
              class="px-4 py-2 rounded bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50"
              :disabled="executing"
              @click="confirmModal"
            >
              {{ executing ? '...' : 'Confirmar' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<style scoped>
.quick-action-buttons {
  animation: fade-in 0.2s ease-out;
}
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
